import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { purchaseAndDynamicsApi } from '../../api/purchaseAndDynamics';
import commentApi from '../../api/comment';
import moment from 'moment';

// 模拟求购数据
const MOCK_PURCHASE_DATA = [
  {
    id: 1,
    title: '求购石佛寺文化纪念品',
    description: '寻找高品质的石佛寺文化纪念品，数量不限',
    contactName: '张三',
    contactPhone: '13800138000',
    status: 'ACTIVE',
    createTime: '2023-06-15 10:30:00',
    updateTime: '2023-06-15 10:30:00'
  },
  {
    id: 2,
    title: '求购佛教工艺画',
    description: '需要独特的佛教主题工艺画，用于展示',
    contactName: '李四',
    contactPhone: '13912345678',
    status: 'INACTIVE',
    createTime: '2023-05-20 14:45:00',
    updateTime: '2023-06-20 09:15:00'
  }
];

// 模拟动态数据
const MOCK_DYNAMICS_DATA = [
  {
    id: 1,
    title: '石佛寺文化展览即将开幕',
    content: '备受期待的石佛寺文化展览将于本月底隆重开幕，诚邀各界人士参与',
    author: '石佛寺文化研究中心',
    imageUrl: 'https://example.com/dynamic-1.jpg',
    status: 'PUBLISHED',
    createTime: '2023-06-15 10:30:00',
    updateTime: '2023-06-15 10:30:00'
  },
  {
    id: 2,
    title: '佛教艺术品收藏交流会',
    content: '本次交流会将汇集国内外佛教艺术品收藏家，共同探讨文化传承',
    author: '文化交流协会',
    imageUrl: 'https://example.com/dynamic-2.jpg',
    status: 'DRAFT',
    createTime: '2023-05-20 14:45:00',
    updateTime: '2023-06-20 09:15:00'
  }
];

// 格式化时间数组为字符串
const formatTimeArray = (timeArray) => {
  if (Array.isArray(timeArray) && timeArray.length >= 6) {
    return moment(new Date(timeArray[0], timeArray[1] - 1, timeArray[2], 
                           timeArray[3], timeArray[4], timeArray[5])).format('YYYY-MM-DD HH:mm:ss');
  }
  return '-';
};

// 获取求购列表
export const fetchPurchaseList = createAsyncThunk(
  'purchaseAndDynamics/fetchPurchaseList',
  async ({ page = 1, pageSize = 10, ...params }, { rejectWithValue }) => {
    try {
      const response = await purchaseAndDynamicsApi.getPurchaseList({ page, pageSize, ...params });
      return response.data;
    } catch (error) {
      console.warn('获取求购列表失败，使用模拟数据', error);
      
      // 模拟搜索功能
      const filteredData = MOCK_PURCHASE_DATA.filter(purchase => {
        const titleMatch = !params.title || 
          purchase.title.toLowerCase().includes(params.title.toLowerCase());
        
        const contactNameMatch = !params.contactName || 
          purchase.contactName.toLowerCase().includes(params.contactName.toLowerCase());
        
        const statusMatch = !params.status || 
          purchase.status === params.status;
        
        const timeMatch = !params.startTime || !params.endTime || 
          (purchase.createTime >= params.startTime && purchase.createTime <= params.endTime);
        
        return titleMatch && contactNameMatch && statusMatch && timeMatch;
      });

      // 模拟分页
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = filteredData.slice(startIndex, endIndex);
      
      return {
        list: paginatedData,
        total: filteredData.length,
        page,
        pageSize
      };
    }
  }
);

// 创建求购
export const createPurchase = createAsyncThunk(
  'purchaseAndDynamics/createPurchase',
  async (purchaseData, { rejectWithValue }) => {
    try {
      const response = await purchaseAndDynamicsApi.createPurchase(purchaseData);
      return response.data;
    } catch (error) {
      console.warn('创建求购失败，使用模拟数据', error);
      const newPurchase = {
        id: MOCK_PURCHASE_DATA.length + 1,
        ...purchaseData,
        createTime: new Date().toLocaleString(),
        updateTime: new Date().toLocaleString()
      };
      MOCK_PURCHASE_DATA.push(newPurchase);
      return newPurchase;
    }
  }
);

// 更新求购
export const updatePurchase = createAsyncThunk(
  'purchaseAndDynamics/updatePurchase',
  async ({ id, ...purchaseData }, { rejectWithValue }) => {
    try {
      const response = await purchaseAndDynamicsApi.updatePurchase(id, purchaseData);
      return response.data;
    } catch (error) {
      console.warn('更新求购失败，使用模拟数据', error);
      const index = MOCK_PURCHASE_DATA.findIndex(p => p.id === id);
      if (index !== -1) {
        const updatedPurchase = { 
          ...MOCK_PURCHASE_DATA[index], 
          ...purchaseData,
          updateTime: new Date().toLocaleString()
        };
        MOCK_PURCHASE_DATA[index] = updatedPurchase;
        return updatedPurchase;
      }
      return rejectWithValue('求购不存在');
    }
  }
);

// 删除求购
export const deletePurchase = createAsyncThunk(
  'purchaseAndDynamics/deletePurchase',
  async (id, { rejectWithValue }) => {
    try {
      await purchaseAndDynamicsApi.deletePurchase(id);
      return id;
    } catch (error) {
      console.warn('删除求购失败，使用模拟数据', error);
      const index = MOCK_PURCHASE_DATA.findIndex(p => p.id === id);
      if (index !== -1) {
        MOCK_PURCHASE_DATA.splice(index, 1);
        return id;
      }
      return rejectWithValue('求购不存在');
    }
  }
);

// 获取动态列表
export const fetchDynamicsList = createAsyncThunk(
  'purchaseAndDynamics/fetchDynamicsList',
  async ({ page = 1, pageSize = 10, ...params }, { rejectWithValue }) => {
    try {
      const response = await purchaseAndDynamicsApi.getDynamicsList({ page, pageSize, ...params });
      return response.data;
    } catch (error) {
      console.warn('获取动态列表失败，使用模拟数据', error);
      
      // 模拟搜索功能
      const filteredData = MOCK_DYNAMICS_DATA.filter(dynamic => {
        const titleMatch = !params.title || 
          dynamic.title.toLowerCase().includes(params.title.toLowerCase());
        
        const authorMatch = !params.author || 
          dynamic.author.toLowerCase().includes(params.author.toLowerCase());
        
        const statusMatch = !params.status || 
          dynamic.status === params.status;
        
        const timeMatch = !params.startTime || !params.endTime || 
          (dynamic.createTime >= params.startTime && dynamic.createTime <= params.endTime);
        
        return titleMatch && authorMatch && statusMatch && timeMatch;
      });

      // 模拟分页
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = filteredData.slice(startIndex, endIndex);
      
      return {
        list: paginatedData,
        total: filteredData.length,
        page,
        pageSize
      };
    }
  }
);

// 创建动态
export const createDynamic = createAsyncThunk(
  'purchaseAndDynamics/createDynamic',
  async (dynamicData, { rejectWithValue }) => {
    try {
      const response = await purchaseAndDynamicsApi.createDynamic(dynamicData);
      return response.data;
    } catch (error) {
      console.warn('创建动态失败，使用模拟数据', error);
      const newDynamic = {
        id: MOCK_DYNAMICS_DATA.length + 1,
        ...dynamicData,
        imageUrl: dynamicData.imageUrl instanceof FormData 
          ? URL.createObjectURL(dynamicData.imageUrl.get('imageUrl')) 
          : dynamicData.imageUrl,
        createTime: new Date().toLocaleString(),
        updateTime: new Date().toLocaleString()
      };
      MOCK_DYNAMICS_DATA.push(newDynamic);
      return newDynamic;
    }
  }
);

// 更新动态
export const updateDynamic = createAsyncThunk(
  'purchaseAndDynamics/updateDynamic',
  async ({ id, ...dynamicData }, { rejectWithValue }) => {
    try {
      const response = await purchaseAndDynamicsApi.updateDynamic(id, dynamicData);
      return response.data;
    } catch (error) {
      console.warn('更新动态失败，使用模拟数据', error);
      const index = MOCK_DYNAMICS_DATA.findIndex(d => d.id === id);
      if (index !== -1) {
        const updatedDynamic = { 
          ...MOCK_DYNAMICS_DATA[index], 
          ...dynamicData,
          imageUrl: dynamicData.imageUrl instanceof FormData 
            ? URL.createObjectURL(dynamicData.imageUrl.get('imageUrl')) 
            : (dynamicData.imageUrl || MOCK_DYNAMICS_DATA[index].imageUrl),
          updateTime: new Date().toLocaleString()
        };
        MOCK_DYNAMICS_DATA[index] = updatedDynamic;
        return updatedDynamic;
      }
      return rejectWithValue('动态不存在');
    }
  }
);

// 删除动态
export const deleteDynamic = createAsyncThunk(
  'purchaseAndDynamics/deleteDynamic',
  async (id, { rejectWithValue }) => {
    try {
      await purchaseAndDynamicsApi.deleteDynamic(id);
      return id;
    } catch (error) {
      console.warn('删除动态失败，使用模拟数据', error);
      const index = MOCK_DYNAMICS_DATA.findIndex(d => d.id === id);
      if (index !== -1) {
        MOCK_DYNAMICS_DATA.splice(index, 1);
        return id;
      }
      return rejectWithValue('动态不存在');
    }
  }
);

// 获取评论列表
export const fetchCommentList = createAsyncThunk(
  'purchaseAndDynamics/fetchCommentList',
  async ({ contentType, contentId, page = 1, pageSize = 10 }, { rejectWithValue }) => {
    try {
      const response = await commentApi.pageComments({
        contentType,
        contentId,
        page,
        pageSize
      });

      // 处理响应数据
      const processedData = {
        records: (response.data?.records || []).map(comment => ({
          ...comment,
          createTime: formatTimeArray(comment.createTime),
          user: comment.user || {},
          childComments: (comment.childComments || []).map(child => ({
            ...child,
            createTime: formatTimeArray(child.createTime),
            user: child.user || {}
          }))
        })),
        total: response.data?.total || 0,
        current: response.data?.current || page,
        size: response.data?.size || pageSize
      };

      return processedData;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取评论列表失败' });
    }
  }
);

// 创建评论
export const createComment = createAsyncThunk(
  'purchaseAndDynamics/createComment',
  async ({ contentType, contentId, content, parentCommentId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await commentApi.addComment({
        contentType,
        contentId,
        content,
        parentCommentId
      });

      // 重新获取评论列表
      dispatch(fetchCommentList({ contentType, contentId }));

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '创建评论失败' });
    }
  }
);

// 删除评论
export const deleteComment = createAsyncThunk(
  'purchaseAndDynamics/deleteComment',
  async ({ contentType, contentId, commentId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await commentApi.deleteComment(commentId);

      // 重新获取评论列表
      dispatch(fetchCommentList({ contentType, contentId }));

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '删除评论失败' });
    }
  }
);

const purchaseAndDynamicsSlice = createSlice({
  name: 'purchaseAndDynamics',
  initialState: {
    purchaseList: [],
    dynamicsList: [],
    commentList: [],
    commentPagination: {
      current: 1,
      pageSize: 10,
      total: 0
    },
    pagination: {
      current: 1,
      pageSize: 10,
      total: 0
    },
    loading: false,
    error: null
  },
  reducers: {
    setPurchaseAndDynamicsPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    }
  },
  extraReducers: (builder) => {
    builder
      // 求购列表
      .addCase(fetchPurchaseList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPurchaseList.fulfilled, (state, action) => {
        state.loading = false;
        state.purchaseList = action.payload.list;
        state.pagination = {
          current: action.payload.page,
          pageSize: action.payload.pageSize,
          total: action.payload.total
        };
      })
      .addCase(fetchPurchaseList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.purchaseList = MOCK_PURCHASE_DATA;
        state.pagination.total = MOCK_PURCHASE_DATA.length;
      })
      // 创建求购
      .addCase(createPurchase.fulfilled, (state, action) => {
        state.purchaseList.push(action.payload);
      })
      // 更新求购
      .addCase(updatePurchase.fulfilled, (state, action) => {
        const index = state.purchaseList.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.purchaseList[index] = action.payload;
        }
      })
      // 删除求购
      .addCase(deletePurchase.fulfilled, (state, action) => {
        state.purchaseList = state.purchaseList.filter(p => p.id !== action.payload);
      })
      // 动态列表
      .addCase(fetchDynamicsList.fulfilled, (state, action) => {
        state.loading = false;
        state.dynamicsList = action.payload.list;
        state.pagination = {
          current: action.payload.page,
          pageSize: action.payload.pageSize,
          total: action.payload.total
        };
      })
      // 创建动态
      .addCase(createDynamic.fulfilled, (state, action) => {
        state.dynamicsList.push(action.payload);
      })
      // 更新动态
      .addCase(updateDynamic.fulfilled, (state, action) => {
        const index = state.dynamicsList.findIndex(d => d.id === action.payload.id);
        if (index !== -1) {
          state.dynamicsList[index] = action.payload;
        }
      })
      // 删除动态
      .addCase(deleteDynamic.fulfilled, (state, action) => {
        state.dynamicsList = state.dynamicsList.filter(d => d.id !== action.payload);
      })
      // 获取评论列表
      .addCase(fetchCommentList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCommentList.fulfilled, (state, action) => {
        state.loading = false;
        state.commentList = action.payload.records;
        state.commentPagination = {
          current: action.payload.current,
          pageSize: action.payload.size,
          total: action.payload.total
        };
      })
      .addCase(fetchCommentList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // 创建评论
      .addCase(createComment.fulfilled, (state, action) => {
        // The commentList and commentPagination are now managed by the new fetchCommentList
        // This case is no longer needed here.
      })
      
      // 删除评论
      .addCase(deleteComment.fulfilled, (state, action) => {
        // The commentList and commentPagination are now managed by the new fetchCommentList
        // This case is no longer needed here.
      });
  }
});

export const { setPurchaseAndDynamicsPagination } = purchaseAndDynamicsSlice.actions;
export default purchaseAndDynamicsSlice.reducer; 